import { motion } from 'framer-motion';

export default function OpenHopeExperienceSection() {
  return (
    <section className="py-24 sm:py-32 bg-white text-[#0F172A] border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-24">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-4xl sm:text-5xl font-bold tracking-tight mb-6"
          >
            Technology Platform
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-[#0F172A]/70 font-light max-w-2xl mx-auto"
          >
            A comprehensive, government-grade safety infrastructure powering the nation's emergency response.
          </motion.p>
        </div>

        <div className="relative group">
          {/* Blur Overlay & Coming Soon Content */}
          <div className="absolute inset-0 z-20 bg-white/60 backdrop-blur-md flex flex-col items-center justify-center rounded-3xl">
            <span className="px-4 py-1.5 bg-[#0F172A] text-white text-sm font-semibold tracking-wider uppercase rounded-full mb-4">Coming Soon</span>
            <a href="/technologies" className="group inline-flex items-center gap-4 px-6 py-3 bg-white text-[#0F172A] text-lg font-bold rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 border border-gray-100">
              <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-100 shrink-0 shadow-inner">
                <img src="https://res.cloudinary.com/ddf1wv47y/image/upload/v1762130174/WhatsApp_Image_2025-11-02_at_02.28.19_1_qk97my.jpg" alt="Open Hope Technologies" className="w-full h-full object-cover" />
              </div>
              <span className="tracking-tight pr-2">Open Hope Technologies</span>
              <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#16A34A] transition-colors">
                <svg className="w-4 h-4 text-gray-400 group-hover:text-white group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 opacity-40 select-none pointer-events-none">
          {/* Dispatch Dashboard - Large Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-2 bg-[#F8FAFC] rounded-3xl p-8 sm:p-12 border border-gray-200 flex flex-col justify-between overflow-hidden relative group"
          >
            <div className="relative z-10 mb-12">
              <h3 className="font-sans text-2xl font-bold mb-3">Dispatch Dashboard</h3>
              <p className="text-[#0F172A]/70 text-lg font-light">Real-time resource allocation and responder coordination across all states.</p>
            </div>
            
            {/* UI Mockup for Dashboard */}
            <div className="relative z-10 w-full aspect-video bg-white rounded-xl shadow-lg border border-gray-100 p-4 flex gap-4 overflow-hidden transform group-hover:-translate-y-2 transition-transform duration-500">
              <div className="w-1/4 h-full flex flex-col gap-3">
                <div className="h-6 w-full bg-gray-100 rounded-md"></div>
                <div className="h-20 w-full bg-[#16A34A]/10 rounded-lg"></div>
                <div className="h-12 w-full bg-gray-50 rounded-lg"></div>
                <div className="h-12 w-full bg-gray-50 rounded-lg"></div>
              </div>
              <div className="w-3/4 h-full flex flex-col gap-4">
                <div className="h-1/2 w-full bg-gray-50 rounded-lg relative overflow-hidden">
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#16A34A 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
                </div>
                <div className="flex gap-4 h-1/2">
                  <div className="w-1/2 h-full bg-gray-50 rounded-lg"></div>
                  <div className="w-1/2 h-full bg-[#0F172A] rounded-lg"></div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mobile Alerts - Tall Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-[#0F172A] text-white rounded-3xl p-8 sm:p-12 flex flex-col justify-between overflow-hidden relative group"
          >
            <div className="relative z-10 mb-12">
              <h3 className="font-sans text-2xl font-bold mb-3">Mobile Alerts</h3>
              <p className="text-white/70 text-lg font-light">Instant, geofenced notifications for citizens in danger zones.</p>
            </div>

            {/* UI Mockup for Mobile App */}
            <div className="relative z-10 mx-auto w-48 h-80 bg-black rounded-3xl border-4 border-gray-800 p-2 transform group-hover:-translate-y-2 transition-transform duration-500">
              <div className="w-full h-full bg-[#16A34A] rounded-2xl p-4 flex flex-col justify-end relative overflow-hidden">
                <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <div className="w-6 h-6 bg-white rounded-full animate-pulse"></div>
                </div>
                <div className="bg-white rounded-xl p-3 mb-2">
                  <div className="w-12 h-2 bg-gray-200 rounded-full mb-2"></div>
                  <div className="w-full h-3 bg-gray-800 rounded-full"></div>
                </div>
                <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
                  <div className="w-full h-2 bg-white/50 rounded-full"></div>
                </div>
              </div>
            </div>
          </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
